import * as React from 'react';
import { useState } from 'react';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutlineOutlined,
  DriveFileMoveOutlined,
  EditOutlined,
  MoreVert,
  SubdirectoryArrowRight,
} from '@mui/icons-material';
import { useAppTranslation } from '../../services/i18n';
import { isAppleKeyboard } from './WorkplaceShortcuts';


export interface WorkplaceRowActions {
  onEdit: () => void;
  onCreateChild: () => void;
  onCreateSibling: () => void;
  onMoveTo: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

interface Props extends WorkplaceRowActions {
  /** the workplace this menu belongs to, for the button's accessible name */
  name: string;
  /** false at the ends of a sibling list, where there is nowhere to move */
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** off drops the two nesting actions and renames the third */
  isTreeEnabled: boolean;
}

/**
 * Everything here is also a keyboard shortcut, but the shortcuts are only discoverable
 * through the minihelp -- with a mouse the tree could otherwise only be reordered by
 * dragging. The shortcut is spelled out beside each item so the menu teaches them.
 */
export const WorkplaceRowMenu: React.FC<Props> = ({
  name,
  canMoveUp,
  canMoveDown,
  isTreeEnabled,
  onEdit,
  onCreateChild,
  onCreateSibling,
  onMoveTo,
  onMoveUp,
  onMoveDown,
  onDelete,
}) => {
  const t = useAppTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const mod = isAppleKeyboard() ? '⌘' : 'Ctrl';

  const close = () => setAnchorEl(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  const items: {
    key: string;
    label: string;
    icon: JSX.Element;
    shortcut?: string;
    onSelect: () => void;
    isDisabled?: boolean;
    isDanger?: boolean;
  }[] = [
    {
      key: 'edit',
      label: t('workplaces.menu.edit'),
      icon: <EditOutlined fontSize={'small'} />,
      shortcut: '↵',
      onSelect: onEdit,
    },
    // a flat list has no "under" to add to or move into, so both of those go away and
    // the remaining one is simply "add a workplace"
    ...(isTreeEnabled
      ? [
          {
            key: 'child',
            label: t('workplaces.menu.addChild'),
            icon: <SubdirectoryArrowRight fontSize={'small'} />,
            shortcut: mod + ' ⇧ ↵',
            onSelect: onCreateChild,
          },
        ]
      : []),
    {
      key: 'sibling',
      label: t(isTreeEnabled ? 'workplaces.menu.addSibling' : 'workplaces.menu.addFlat'),
      icon: <Add fontSize={'small'} />,
      shortcut: mod + ' ↵',
      onSelect: onCreateSibling,
    },
    ...(isTreeEnabled
      ? [
          {
            key: 'moveTo',
            label: t('workplaces.menu.moveTo'),
            icon: <DriveFileMoveOutlined fontSize={'small'} />,
            onSelect: onMoveTo,
          },
        ]
      : []),
    {
      key: 'up',
      label: t('workplaces.menu.moveUp'),
      icon: <ArrowUpward fontSize={'small'} />,
      shortcut: 'Alt ↑',
      onSelect: onMoveUp,
      isDisabled: !canMoveUp,
    },
    {
      key: 'down',
      label: t('workplaces.menu.moveDown'),
      icon: <ArrowDownward fontSize={'small'} />,
      shortcut: 'Alt ↓',
      onSelect: onMoveDown,
      isDisabled: !canMoveDown,
    },
    {
      key: 'delete',
      label: t('workplaces.menu.delete'),
      icon: <DeleteOutlineOutlined fontSize={'small'} />,
      shortcut: 'Delete',
      onSelect: onDelete,
      isDanger: true,
    },
  ];

  return (
    <>
      <IconButton
        size={'small'}
        className={'workplace-row__menu-button'}
        aria-label={t('workplaces.menu.open', { name })}
        aria-haspopup={'menu'}
        aria-expanded={!!anchorEl}
        onClick={(event) => {
          // the row underneath would otherwise select and, on a second click, open
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreVert fontSize={'small'} />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // the tree owns these keys; inside the menu they belong to the menu
        onKeyDown={(event) => event.stopPropagation()}
      >
        {items.map((item) => [
          // destruction is last and set apart, so it is never the neighbour of a move
          item.isDanger ? <Divider key={item.key + '-divider'} /> : null,
          <MenuItem
            key={item.key}
            disabled={item.isDisabled}
            onClick={run(item.onSelect)}
            className={item.isDanger ? 'workplace-row__menu-item--danger' : undefined}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
            {!!item.shortcut && (
              <span className={'workplace-row__menu-shortcut'}>{item.shortcut}</span>
            )}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};
